// Home hero. Replaces the empty 300px banner left over from the Purity UI profile template.
// The right-hand panel is a simulated camera feed (see LiveView.js) showing what the products
// actually do — line-cross analytics, object detection, plate reading.
import { Box, Button, Flex, Grid, Text } from "@chakra-ui/react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LiveView from "./LiveView";

// The simulated feed panel is parked until real camera stills are available — the drawn
// vector scenes did not read as convincing footage. To bring it back:
//   1. put line-cross.jpg / object-detection.jpg / license-plate.jpg in public/scenes/
//      (see docs/hero-scenes.md for the shot list)
//   2. flip this to true
// LiveView picks the stills up on its own and keeps the vector overlay on top of them.
const SHOW_LIVE_VIEW = false;

const Hero = () => {
  const { t } = useTranslation("home");

  return (
    <Box
      position="relative"
      overflow="hidden"
      bgColor="brand.900"
      bgImage={
        "radial-gradient(120% 100% at 88% 8%, rgba(43, 143, 212, 0.30) 0%, rgba(43, 143, 212, 0) 58%), " +
        "radial-gradient(90% 80% at 8% 96%, rgba(25, 65, 143, 0.55) 0%, rgba(25, 65, 143, 0) 62%), " +
        "linear-gradient(168deg, #0C1426 0%, #0A1020 54%, #080D18 100%)"
      }
      px={{ base: "26px", md: "44px" }}
      /* The navbar floats over this section rather than pushing it down, so the top padding has
         to clear it. It sits 18px from the panel top and is 75px tall, which lands 63px into
         this box once PanelContainer's own 30px padding is accounted for. */
      pt={SHOW_LIVE_VIEW ? { base: "96px", md: "52px" } : { base: "104px", md: "72px" }}
      pb={SHOW_LIVE_VIEW ? { base: "38px", md: "52px" } : { base: "48px", md: "72px" }}
    >
      <Box
        position="absolute"
        inset="0"
        pointerEvents="none"
        bgImage="radial-gradient(58% 58% at 84% 44%, rgba(43, 143, 212, 0.13) 0%, rgba(0, 0, 0, 0) 70%)"
      />
      <Grid
        position="relative"
        templateColumns={SHOW_LIVE_VIEW ? { base: "1fr", lg: "1.02fr 0.98fr" } : "1fr"}
        gap={{ base: "30px", lg: "44px" }}
        alignItems="center"
      >
        <Box maxW={SHOW_LIVE_VIEW ? "none" : "58ch"}>
          <Flex align="center" gap="9px" mb="20px">
            <Box w="22px" h="1px" bg="brand.400" flex="none" />
            <Text
              fontFamily="mono"
              fontSize="11px"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#7FC0F0"
            >
              {t("hero.eyebrow")}
            </Text>
          </Flex>

          <Text
            as="h1"
            color="white"
            fontWeight="700"
            fontSize={{ base: "28px", md: "36px", xl: "42px" }}
            lineHeight="1.14"
            letterSpacing="-0.035em"
            mb="16px"
          >
            {t("hero.titleTop")}
            <br />
            <Text as="span" color="#7FC0F0">
              {t("hero.titleBottom")}
            </Text>
          </Text>

          <Text color="#9FB0C8" fontSize="15px" lineHeight="1.75" maxW="44ch" mb="28px">
            {t("hero.description")}
          </Text>

          <Flex gap="11px" flexWrap="wrap">
            <Button
              as={RouterLink}
              to="/allproducts"
              h="44px"
              px="22px"
              borderRadius="3px"
              fontSize="13.5px"
              fontWeight="700"
              color="white"
              bgImage="linear-gradient(135deg, #2B8FD4, #19418F)"
              _hover={{ bgImage: "linear-gradient(135deg, #3C9EE0, #1D4CA5)", textDecoration: "none" }}
              _active={{ bgImage: "linear-gradient(135deg, #2681C2, #163A80)" }}
            >
              {t("hero.ctaPrimary")} →
            </Button>
            <Button
              as={RouterLink}
              to="/contactus"
              h="44px"
              px="22px"
              borderRadius="3px"
              fontSize="13.5px"
              fontWeight="700"
              variant="outline"
              color="#DCE6F4"
              borderColor="rgba(255, 255, 255, 0.26)"
              bg="transparent"
              _hover={{ bg: "whiteAlpha.100", textDecoration: "none" }}
            >
              {t("hero.ctaSecondary")}
            </Button>
          </Flex>
        </Box>

        {SHOW_LIVE_VIEW && <LiveView channel={t("hero.monitorChannel")} />}
      </Grid>
    </Box>
  );
};

export default Hero;
